<template>
  <aside id="inbox-summary">
    <header class="summary-header">
      <b-avatar :text="initials" size="2.5rem" />
      <div class="summary-identity">
        <h3 class="name">{{ fullName || 'Unknown customer' }}</h3>
        <p v-if="customerSubtitle" class="subtitle">{{ customerSubtitle }}</p>
      </div>
      <router-link
        v-if="customer && customer.id && merchantId"
        :to="customerDetailRoute"
        class="open-link"
        title="Open customer record"
        @click.native="primeCurrentAccount"
      >
        <ArrowUpRight :size="16" />
      </router-link>
    </header>

    <div v-if="hasMultipleAccounts" class="account-switcher">
      <label for="inbox-account-picker">Account</label>
      <b-form-select
        id="inbox-account-picker"
        size="sm"
        :value="currentAccountId"
        :options="accountOptions"
        @change="onAccountChange"
      />
    </div>

    <div class="badge-row">
      <span v-if="account" :class="statusPillClass">{{ statusLabel }}</span>
      <span v-if="agingBadge" :class="agingBadge.cls">{{
        agingBadge.label
      }}</span>
      <span
        v-if="textabilityPill"
        :class="textabilityPill.cls"
        :title="textabilityPill.detail"
      >
        {{ textabilityPill.label }}
      </span>
    </div>

    <div v-if="account && account.id" class="action-stack">
      <div v-if="isIntegrated" class="action-group">
        <b-button
          class="action-btn"
          :disabled="isUpdatingDms || updateCooldown"
          :title="
            updateCooldown
              ? 'Just refreshed - try again in a moment'
              : `Pull fresh account data from ${integrationName}`
          "
          @click="refreshFromDms"
        >
          <b-spinner v-if="isUpdatingDms" small />
          <RefreshCcw v-else :size="14" />
          <span>Update Now</span>
        </b-button>
        <p v-if="lastDmsUpdateHint" class="action-hint">
          {{ lastDmsUpdateHint }}
        </p>
      </div>

      <div class="action-group">
        <b-button
          class="action-btn"
          :disabled="isMarkingWorked"
          :title="workedHint || 'Mark this account as worked'"
          @click="markWorked"
        >
          <b-spinner v-if="isMarkingWorked" small />
          <CheckCircle2 v-else :size="14" />
          <span>Worked</span>
        </b-button>
        <p v-if="workedHint" class="action-hint">{{ workedHint }}</p>
      </div>
    </div>

    <section v-if="contactRows.length" class="summary-section">
      <h4>Contact</h4>
      <dl class="kv">
        <template v-for="row in contactRows">
          <dt :key="`k-${row.label}`">{{ row.label }}</dt>
          <dd :key="`v-${row.label}`" class="ellipsis">{{ row.value }}</dd>
        </template>
      </dl>
    </section>

    <section v-if="account" class="summary-section">
      <h4>Account</h4>
      <dl class="kv">
        <template v-for="row in accountRows">
          <dt :key="`ak-${row.label}`">{{ row.label }}</dt>
          <dd :key="`av-${row.label}`" :class="row.cls || 'ellipsis'">
            {{ row.value }}
          </dd>
        </template>
      </dl>
    </section>

    <section v-if="activePromise" class="summary-section promise">
      <h4>Active promise</h4>
      <p class="line">
        <span class="amount">{{ moneyFormatter(activePromise.amount) }}</span>
        <span class="muted"> by {{ formatDate(activePromise.date) }}</span>
      </p>
      <span :class="activePromise.confirmedCls">{{
        activePromise.confirmedLabel
      }}</span>
    </section>

    <section v-if="scheduledPayment" class="summary-section">
      <h4>Scheduled payment</h4>
      <p class="line">
        <span v-if="scheduledPayment.amount != null" class="amount">
          {{ moneyFormatter(scheduledPayment.amount) }}
        </span>
        <span v-if="scheduledPayment.date" class="muted">
          on {{ formatDate(scheduledPayment.date) }}
        </span>
      </p>
      <p v-if="scheduledPayment.method" class="muted small">
        {{ scheduledPayment.method }}
      </p>
    </section>

    <section
      v-if="account && (isLoadingPayments || recentPayments.length)"
      class="summary-section"
    >
      <h4>Recent payments</h4>
      <div v-if="isLoadingPayments" class="payments-loading">
        <b-spinner small />
        <span>Loading payments…</span>
      </div>
      <ul v-else class="payment-list">
        <li
          v-for="(p, i) in recentPayments"
          :key="`pmt-${i}`"
          class="payment-row"
        >
          <div class="payment-row__top">
            <span class="amount">{{ moneyFormatter(p.amount) }}</span>
            <span :class="p.statusCls">{{ p.statusLabel }}</span>
          </div>
          <div v-if="p.subtitle" class="muted small">{{ p.subtitle }}</div>
        </li>
      </ul>
    </section>

    <section v-if="lastNote" class="summary-section">
      <h4>Last note</h4>
      <p class="line clamp">{{ lastNote.body }}</p>
      <p v-if="lastNote.subtitle" class="muted small">
        {{ lastNote.subtitle }}
      </p>
    </section>

    <section v-if="lastAiCall" class="summary-section">
      <h4>Last AI call</h4>
      <p class="line">{{ lastAiCall.result }}</p>
      <p v-if="lastAiCall.subtitle" class="muted small">
        {{ lastAiCall.subtitle }}
      </p>
    </section>

    <section v-if="tags.length" class="summary-section">
      <h4>Tags</h4>
      <div class="tag-row">
        <span v-for="tag in tags" :key="tag" class="tag-chip">{{ tag }}</span>
      </div>
    </section>

    <section v-if="!account" class="summary-section">
      <p class="placeholder">No account linked to this customer yet.</p>
    </section>
  </aside>
</template>

<script>
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { BAvatar, BButton, BFormSelect, BSpinner } from 'bootstrap-vue'
import moment from 'moment'
import { ArrowUpRight, CheckCircle2, RefreshCcw } from 'lucide-vue'
import {
  moneyFormatter,
  phoneFormatter,
  standardFormatter
} from '@/utils/formatters'
import { integrationLabel } from '@/utils/integrationLabels'

@Component({
  components: {
    BAvatar,
    BButton,
    BFormSelect,
    BSpinner,
    ArrowUpRight,
    CheckCircle2,
    RefreshCcw
  },
  methods: { moneyFormatter }
})
export default class InboxCustomerSummary extends Vue {
  @Prop({ type: Object, default: null }) customer
  @Prop({ type: Object, default: null }) account
  @Prop({ type: [String, Number], default: null }) selectedAccountId
  @Prop({ type: String, default: '' }) merchantId

  recentPaymentsData = []
  isLoadingPayments = false
  isMarkingWorked = false
  workedAt = null
  isUpdatingDms = false
  updateCooldown = false
  cooldownTimer = null
  paymentsAbortController = null

  @Watch('account.id', { immediate: true })
  onAccountIdChange(newId) {
    if (this.paymentsAbortController) {
      this.paymentsAbortController.abort()
      this.paymentsAbortController = null
    }
    this.workedAt = null
    if (newId && this.merchantId) {
      this.fetchRecentPayments()
    } else {
      this.recentPaymentsData = []
      this.isLoadingPayments = false
    }
  }

  async fetchRecentPayments() {
    if (!this.account?.id || !this.merchantId) return
    const accountAtStart = this.account.id
    const controller = new AbortController()
    this.paymentsAbortController = controller
    this.recentPaymentsData = []
    this.isLoadingPayments = true
    try {
      const response = await this.axios.get(
        this.$endpoints.listPaymentHistoryByAccount(
          this.merchantId,
          this.account.id
        ),
        { params: { page: 1, page_size: 3 }, signal: controller.signal }
      )
      if (this.account?.id !== accountAtStart) return
      this.recentPaymentsData = response?.data?.results || []
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return
      }
      if (this.account?.id === accountAtStart) {
        this.recentPaymentsData = []
      }
    } finally {
      if (this.paymentsAbortController === controller) {
        this.paymentsAbortController = null
        this.isLoadingPayments = false
      }
    }
  }

  async markWorked() {
    if (!this.account?.id || !this.merchantId || this.isMarkingWorked) return
    this.isMarkingWorked = true
    try {
      await this.axios.post(
        this.$endpoints.markWorked(this.merchantId, this.account.id)
      )
      // Optimistic: the click succeeded, so the action just happened. The backend mark_worked endpoint is
      // idempotent within a 12-hour window per collector, so its response may echo an older last_worked.
      // Reflect the actual click time in the UI.
      this.workedAt = new Date().toISOString()
      this.$toasted.show('Account marked as worked', {
        duration: 2000,
        position: 'bottom-center'
      })
    } catch (err) {
      const msg =
        err?.response?.data?.non_field_errors?.[0] ||
        (err?.response
          ? `${err.response.status} - ${err.response.statusText}`
          : 'Could not mark worked')
      this.$toasted.show(msg, {
        duration: 3000,
        position: 'bottom-center',
        type: 'error'
      })
    } finally {
      this.isMarkingWorked = false
    }
  }

  get isIntegrated() {
    return !!this.$store?.getters?.currentMerchant?.is_integrated
  }

  get accountManagerName() {
    const id = this.account?.account_manager_id
    if (id == null) return ''
    const managers =
      this.$store?.getters?.currentMerchant?.account_managers || []
    const am = managers.find(m => m.pkid === id)
    if (!am) return ''

    return (
      am.full_name ||
      [am.first_name, am.last_name].filter(Boolean).join(' ') ||
      am.email ||
      ''
    )
  }

  get integrationName() {
    return integrationLabel(this.$store?.getters?.currentMerchant)
  }

  get lastDmsUpdateHint() {
    const when = this.account?.last_external_update
    if (!when) return `No ${this.integrationName} sync recorded yet`
    const m = moment(when)
    if (!m.isValid()) return ''

    return `${this.integrationName} synced ${m.fromNow()}`
  }

  async refreshFromDms() {
    if (
      !this.customer?.id ||
      !this.isIntegrated ||
      this.isUpdatingDms ||
      this.updateCooldown
    ) {
      return
    }
    this.isUpdatingDms = true
    try {
      await this.$store.dispatch('getCustomer', {
        customerID: this.customer.id,
        checkIntegration: true
      })
      this.$toasted.show(`${this.integrationName} sync complete`, {
        duration: 2000,
        position: 'bottom-center'
      })
    } catch (err) {
      const msg =
        err?.response?.data?.non_field_errors?.[0] ||
        (err?.response
          ? `${err.response.status} - ${err.response.statusText}`
          : `${this.integrationName} sync failed`)
      this.$toasted.show(msg, {
        duration: 3000,
        position: 'bottom-center',
        type: 'error'
      })
    } finally {
      this.isUpdatingDms = false
      this.startUpdateCooldown()
    }
  }

  startUpdateCooldown() {
    if (this.cooldownTimer) clearTimeout(this.cooldownTimer)
    this.updateCooldown = true
    this.cooldownTimer = setTimeout(() => {
      this.updateCooldown = false
      this.cooldownTimer = null
    }, 60000)
  }

  beforeDestroy() {
    if (this.cooldownTimer) {
      clearTimeout(this.cooldownTimer)
      this.cooldownTimer = null
    }
    if (this.paymentsAbortController) {
      this.paymentsAbortController.abort()
      this.paymentsAbortController = null
    }
  }

  get workedHint() {
    if (this.isMarkingWorked) return ''
    const when =
      this.workedAt ||
      this.account?.last_worked?.worked_at ||
      this.account?.worked_at
    if (!when) return ''
    const m = moment(when)
    if (!m.isValid()) return ''
    const who = this.account?.last_worked?.worked_by_name
    const suffix = who && !this.workedAt ? ` by ${who}` : ''

    return `Worked ${m.fromNow()}${suffix}`
  }

  get scheduledPayment() {
    const sp =
      this.account?.scheduled_payment ||
      this.account?.next_scheduled_payment ||
      null
    if (!sp) return null
    const amount = sp.total_amount ?? sp.amount ?? null
    const date = sp.scheduled_date || sp.date || sp.created || ''
    if (amount == null && !date) return null
    let method = ''
    if (sp.payment_method?.label) method = sp.payment_method.label
    else if (sp.last_four) method = `•••• ${sp.last_four}`

    return { amount, date, method }
  }

  get fullName() {
    if (!this.customer) return ''
    const parts = [this.customer.first_name, this.customer.last_name]
      .filter(Boolean)
      .join(' ')
      .trim()

    return parts || this.customer.full_name || ''
  }

  get initials() {
    const f = this.customer?.first_name?.[0] || ''
    const l = this.customer?.last_name?.[0] || ''

    return (f + l).toUpperCase() || '?'
  }

  get customerSubtitle() {
    const externalId =
      this.account?.external_id || this.customer?.external_id || ''
    if (!externalId) return ''

    return `Account ${externalId}`
  }

  get hasMultipleAccounts() {
    return (this.customer?.accounts?.length || 0) > 1
  }

  get currentAccountId() {
    return this.selectedAccountId || this.account?.id || null
  }

  get accountOptions() {
    const accounts = this.customer?.accounts || []

    return accounts.map(a => ({
      value: a.id,
      text: a.name || a.external_id || `Account ${a.id}`
    }))
  }

  onAccountChange(id) {
    this.$emit('account-change', id)
  }

  primeCurrentAccount() {
    if (this.account?.id) {
      this.$store.commit('setCurrentAccount', this.account)
    }
  }

  get contactRows() {
    if (!this.customer) return []
    const rows = []
    if (this.customer.sms_phone_number) {
      rows.push({
        label: 'Phone',
        value: phoneFormatter(this.customer.sms_phone_number)
      })
    }
    if (this.customer.email) {
      rows.push({ label: 'Email', value: this.customer.email })
    }
    // No Language row: the merchant serializer returns the raw Customer.language
    // column, which nothing writes — ui-consumer's language setting goes to
    // User.language — so it read "en-US" for nearly every customer.

    return rows
  }

  get accountRows() {
    if (!this.account) return []
    const rows = []

    const amountDue = this.amountDue
    if (amountDue != null) {
      rows.push({
        label: 'Amount due',
        value: moneyFormatter(amountDue),
        cls: 'amount'
      })
    }

    const dueDate = this.dueDate
    if (dueDate) {
      const days = moment(dueDate).diff(moment().startOf('day'), 'days')
      let dueLabel = moment(dueDate).format('MMM D, YYYY')
      if (days < 0) dueLabel += ` (${Math.abs(days)} days overdue)`
      else if (days === 0) dueLabel += ' (today)'
      else if (days <= 7)
        dueLabel += ` (in ${days} day${days === 1 ? '' : 's'})`
      rows.push({ label: 'Due', value: dueLabel })
    }

    if (this.accountManagerName) {
      rows.push({ label: 'Account mgr', value: this.accountManagerName })
    }

    if (this.account.merchant_location?.name) {
      rows.push({
        label: 'Location',
        value: this.account.merchant_location.name
      })
    }

    if (this.account.autopay) {
      rows.push({ label: 'Autopay', value: 'On' })
    }

    return rows
  }

  get amountDue() {
    const candidates = [
      this.account?.current_invoice?.amount_due,
      this.account?.amount_due,
      this.account?.total_balance
    ]
    for (const value of candidates) {
      if (value != null) return value
    }

    return null
  }

  get dueDate() {
    return (
      this.account?.current_invoice?.due_date || this.account?.due_date || ''
    )
  }

  get statusLabel() {
    const raw = this.account?.status
    if (!raw) return '—'

    return standardFormatter(raw)
  }

  get statusPillClass() {
    const key = (this.account?.status || '').toUpperCase()
    if (key === 'ACTIVE') return 'status-pill status-pill--success'
    if (key === 'PENDING') return 'status-pill status-pill--warning'
    if (key === 'INACTIVE' || key === 'CLOSED')
      return 'status-pill status-pill--neutral'
    if (key === 'DELINQUENT' || key === 'PAST_DUE')
      return 'status-pill status-pill--danger'

    return 'status-pill'
  }

  get agingBadge() {
    const days = this.daysOverdue
    if (days == null || days <= 0) return null
    let label
    let cls = 'status-pill status-pill--warning'
    if (days <= 30) label = '1-30 days late'
    else if (days <= 60) {
      label = '31-60 days late'
      cls = 'status-pill status-pill--warning'
    } else if (days <= 90) {
      label = '61-90 days late'
      cls = 'status-pill status-pill--danger'
    } else {
      label = `${days}+ days late`
      cls = 'status-pill status-pill--danger'
    }

    return { label, cls }
  }

  get daysOverdue() {
    const explicit =
      this.account?.days_overdue ??
      this.account?.overdue_days ??
      this.account?.current_invoice?.days_overdue
    if (explicit != null) return Number(explicit)
    if (!this.dueDate) return null
    const diff = moment().startOf('day').diff(moment(this.dueDate), 'days')

    return diff > 0 ? diff : 0
  }

  get textabilityPill() {
    const dnc =
      this.account?.do_not_contact || this.customer?.do_not_contact || false
    if (dnc) {
      return {
        label: 'Do not contact',
        detail: 'Customer is flagged do-not-contact',
        cls: 'status-pill status-pill--danger'
      }
    }
    const canText = this.canText
    if (canText === false) {
      return {
        label: 'Opted out of SMS',
        detail: 'Customer opted out of text messaging',
        cls: 'status-pill status-pill--danger'
      }
    }

    // Texting is allowed by default; only surface a pill when something restricts it (opt-out or
    // do-not-contact). Hides "Texting allowed" and "Texting unknown" since the agent assumes it's allowed
    // unless shown otherwise.
    return null
  }

  get canText() {
    const candidates = [
      this.customer?.can_text,
      this.account?.can_text,
      this.customer?.opt_in,
      this.customer?.sms_opt_in
    ]
    for (const v of candidates) {
      if (v === true || v === false) return v
      if (v === 1) return true
      if (v === 0) return false
    }

    return null
  }

  get activePromise() {
    const promise =
      this.account?.active_promise ||
      this.account?.payment_promise ||
      this.account?.promise ||
      (Array.isArray(this.account?.promises) ? this.account.promises[0] : null)
    if (!promise) return null
    const amount = promise.amount ?? promise.promise_amount ?? null
    const date = promise.date || promise.promise_date || promise.created || ''
    if (amount == null && !date) return null
    const confirmed =
      promise.confirmed ??
      promise.is_confirmed ??
      (promise.status || '').toUpperCase() === 'CONFIRMED'

    return {
      amount,
      date,
      confirmedLabel: confirmed ? 'Confirmed' : 'Unconfirmed',
      confirmedCls: confirmed
        ? 'status-pill status-pill--success'
        : 'status-pill status-pill--warning'
    }
  }

  get recentPayments() {
    const list = this.recentPaymentsData.length
      ? this.recentPaymentsData
      : this.fallbackPaymentList

    return list
      .slice(0, 3)
      .map(raw => this.normalizePayment(raw))
      .filter(p => p.amount != null || p.subtitle)
  }

  get fallbackPaymentList() {
    const sources = [
      this.account?.recent_payments,
      this.account?.payments,
      this.customer?.recent_payments,
      this.customer?.payments,
      this.account?.last_payment ? [this.account.last_payment] : null,
      this.customer?.last_payment ? [this.customer.last_payment] : null
    ]

    return sources.find(s => Array.isArray(s) && s.length) || []
  }

  normalizePayment(raw) {
    const payment = raw?.payment || raw || {}
    const amount = raw?.amount ?? payment?.amount ?? raw?.total ?? null
    const date =
      payment?.processed_time ||
      payment?.scheduled_date ||
      payment?.created ||
      raw?.date ||
      raw?.created ||
      ''
    let method = ''
    if (payment?.payment_method?.label) method = payment.payment_method.label
    else if (raw?.payment_method?.label) method = raw.payment_method.label
    else if (raw?.last_four) method = `•••• ${raw.last_four}`
    else if (raw?.method) method = raw.method
    else if (payment?.payment_origin_display)
      method = payment.payment_origin_display
    const statusRaw =
      raw?.status ||
      payment?.status ||
      (raw?.succeeded === true
        ? 'SUCCESS'
        : raw?.succeeded === false
          ? 'FAILED'
          : '')
    const statusLabel = statusRaw ? standardFormatter(String(statusRaw)) : '—'
    const statusKey = String(statusRaw || '').toUpperCase()
    let statusCls = 'status-pill status-pill--neutral'
    if (
      statusKey.includes('SUCCESS') ||
      statusKey.includes('COMPLETE') ||
      statusKey.includes('SETTLED') ||
      statusKey === 'PAID'
    ) {
      statusCls = 'status-pill status-pill--success'
    } else if (
      statusKey.includes('PENDING') ||
      statusKey.includes('PROCESS') ||
      statusKey.includes('REVIEW')
    ) {
      statusCls = 'status-pill status-pill--warning'
    } else if (
      statusKey.includes('FAIL') ||
      statusKey.includes('DECLIN') ||
      statusKey.includes('RETURN') ||
      statusKey.includes('REFUND') ||
      statusKey.includes('CANCEL')
    ) {
      statusCls = 'status-pill status-pill--danger'
    }
    const subtitle = [date ? this.formatDate(date) : '', method]
      .filter(Boolean)
      .join(' · ')

    return { amount, statusLabel, statusCls, subtitle }
  }

  get lastNote() {
    const note =
      this.account?.last_note ||
      this.customer?.last_note ||
      (Array.isArray(this.account?.notes) ? this.account.notes[0] : null)
    if (!note) return null
    const body = note.body || note.text || note.message || ''
    if (!body) return null
    const who =
      note.created_by_name ||
      note.created_by?.full_name ||
      [note.created_by?.first_name, note.created_by?.last_name]
        .filter(Boolean)
        .join(' ') ||
      ''
    const when = note.created || note.date || ''
    const subtitle = [who, when ? this.formatDate(when) : '']
      .filter(Boolean)
      .join(' · ')

    return { body, subtitle }
  }

  get lastAiCall() {
    const call =
      this.account?.last_ai_call ||
      this.customer?.last_ai_call ||
      (Array.isArray(this.account?.ai_calls) ? this.account.ai_calls[0] : null)
    if (!call) return null
    const rawResult =
      call.disconnection_reason || call.result || call.status || ''
    if (!rawResult) return null
    const result = standardFormatter(rawResult)
    const when = call.date || call.created || call.call_time || ''
    const duration =
      call.duration_seconds != null
        ? `${Math.round(call.duration_seconds)}s`
        : ''
    const subtitle = [when ? this.formatDate(when) : '', duration]
      .filter(Boolean)
      .join(' · ')

    return { result, subtitle }
  }

  formatDate(value) {
    if (!value) return ''
    const m = moment(value)
    if (!m.isValid()) return String(value)

    return m.format('MMM D, YYYY')
  }

  get tags() {
    const accountTags = this.account?.tags || []
    const customerTags = this.customer?.tags || []
    const all = [...accountTags, ...customerTags].map(t =>
      typeof t === 'string' ? t : t?.name || t?.slug || ''
    )

    return all.filter(Boolean)
  }

  get customerDetailRoute() {
    return {
      name: 'CustomerAccountDetails',
      params: {
        customerID: this.customer.id,
        merchantID: this.merchantId
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/variables';

#inbox-summary {
  display: flex;
  flex: 0 0 300px;
  flex-direction: column;
  gap: 12px;
  width: 400px;
  min-width: 360px;
  max-width: 440px;
  height: 100%;
  padding: 16px;
  overflow-y: auto;
  background: $customer-detail-bg;
  border-left: 1px solid rgba(0, 0, 0, 0.2);
}

.summary-header {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 4px 0 0;
}

.summary-identity {
  flex: 1;
  min-width: 0;
}

.name {
  margin: 0;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.05rem;
  font-weight: 600;
}

.subtitle {
  margin: 2px 0 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 12.5px;
}

.open-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  color: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
  text-decoration: none;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
  }
}

.account-switcher {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);

  label {
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  ::v-deep .custom-select {
    flex: 1;
    background-color: #2b2f36;
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 6px;
    background-image:
      linear-gradient(45deg, transparent 50%, #fff 50%),
      linear-gradient(135deg, #fff 50%, transparent 50%);
    background-position:
      calc(100% - 14px) 50%,
      calc(100% - 9px) 50%;
    background-size:
      5px 5px,
      5px 5px;
    background-repeat: no-repeat;

    &:focus {
      border-color: #4f8cff;
      box-shadow: 0 0 0 2px rgba(79, 140, 255, 0.25);
    }

    option {
      background-color: #2b2f36;
      color: #fff;
    }
  }
}

.payments-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  padding: 4px 0;
}

.badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0;
}

.action-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.action-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-btn {
  display: inline-flex;
  flex: 1;
  gap: 6px;
  align-items: center;
  justify-content: center;
  padding: 7px 10px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.95);
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.3);
    color: #fff;
    text-decoration: none;
  }

  &:disabled,
  &.disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

.action-hint {
  margin: -4px 2px 0;
  color: rgba(255, 255, 255, 0.55);
  font-size: 11.5px;
}

.summary-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: $customer-detail-light-bg;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;

  h4 {
    margin: 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
}

.kv {
  display: grid;
  grid-template-columns: minmax(86px, auto) 1fr;
  gap: 6px 12px;
  margin: 0;

  dt {
    margin: 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 12.5px;
    font-weight: 500;
  }

  dd {
    margin: 0;
    color: rgba(255, 255, 255, 0.95);
    font-size: 14px;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .amount {
    color: rgba(255, 255, 255, 0.95);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
}

.line {
  margin: 0;
  color: rgba(255, 255, 255, 0.95);
  font-size: 14px;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.line .amount {
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.payment-row__top .amount {
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.muted {
  color: rgba(255, 255, 255, 0.65);
}

.small {
  font-size: 12.5px;
}

.clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ellipsis {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag-chip {
  padding: 3px 9px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 11.5px;
  font-weight: 500;
}

.placeholder {
  margin: 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  font-style: italic;
}

.payment-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.payment-row {
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  &:first-child {
    padding-top: 0;
    border-top: none;
  }

  &__top {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: space-between;
  }
}

.status-pill {
  display: inline-block;
  padding: 0.15em 0.6em;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.95);

  &--success {
    background: rgba(0, 190, 175, 0.22);
    color: #5fe5d3;
  }
  &--warning {
    background: rgba(253, 185, 19, 0.22);
    color: #ffd566;
  }
  &--neutral {
    background: rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.85);
  }
  &--danger {
    background: rgba(236, 17, 105, 0.22);
    color: #ff7da9;
  }
}
</style>
